import type { ParcelRecord, ScanOutcome } from "./driver-api.ts";
import type { ChipClass } from "./shift-readiness.ts";

/**
 * `5 Scan` — the scan this device made, and what LOG-06 did with it.
 *
 * The design's screen, value for value from `11 Driver App.dc.html`:
 *
 *     scan:{l:'5 Scan',art:'scan',on:1,chip:['done','scan confirmed'],
 *       title:'Parcel par_3N0E is with you',
 *       sub:'Scan 1146 · next: Oyster Bay, 3.1 km',
 *       btns:[['Start delivery','navigation',1,'slide']]}
 *
 * `tests/contract/mobile-screen-support.test.ts` marks it **degraded** with exactly one element
 * missing, and it is the second half of the sub-line:
 *
 *   > `next: Oyster Bay, 3.1 km` — nothing publishes `orders.deliveryAddressLabel` or
 *   > `order.dropoff.formattedAddress`
 *
 * **That absence survived ADR 0035, and it is worth saying why, because the screen before this one
 * is the thing that decision closed.** `4 To pickup` holds an `orderId`, DEL-03 publishes
 * `order.pickupAddressId` *and* `order.deliveryAddressId`, and GEO-02 resolves either. This screen
 * holds a **parcel**: LOG-02's record publishes a merchant, an external reference, a description, a
 * weight, dimensions, handling flags and a status, and nothing that names an order, a shipment's
 * destination or a place. So the next stop is not a field that is missing from a record this screen
 * reads — it is absent from the service this screen's subject belongs to, and no address read can
 * reach it from a parcel identifier.
 *
 * So the sub-line says it in words. The design's half that has a source is kept verbatim —
 * `Scan 1146` is this device's own `sourceSequence`, which is the number LOG-06 orders scans by —
 * and the half that does not becomes a sentence naming what is missing. Never a plausible place
 * name, never a distance, never `next: —`.
 *
 * ## Why this screen's subject is the device and not a read
 *
 * The mobile sweep's first element behind this screen is a **write**: `POST /v1/device/scans`, with
 * no identifier it has to be handed first. That is the same shape as `6 Offline queue`, whose
 * subject is this handset. A scan's `shipmentId` and `parcelId` come off the label the courier
 * pointed the camera at, and no platform read produces them — which is exactly why LOG-06's scan
 * carries `scanId` and `sourceSequence` of the sender's own and takes no `Idempotency-Key`.
 *
 * In a browser there is no camera, so the two identifiers are read from the address —
 * `?shipment=<uuid>&parcel=<uuid>` — which is what the device would hand the screen. Same
 * arrangement as `?proof=` on `8 Proof` and `?offers=` on screen 2, and recorded for the same
 * reason: this is where a barcode reader plugs in. Q-127.
 */

/**
 * The scope the parcel read spends.
 *
 * LOG-02 bounds it by the verified tenant and nothing else, and ADR 0038 decided that is **correct**
 * rather than outstanding: a parcel record names a merchant, which is a business party and not a
 * token subject, and custody of a parcel is a LOG-06 scan in a different service — so there is no
 * subject to bound this read by and inventing one would make a read's authorization depend on
 * another service being up. R-48 found it beside DRV-04's vehicle read; the two were the same
 * finding and are not the same case. R-51.
 */
export const PARCEL_READ_SCOPE = "parcel:read";

/**
 * The scan type recorded when the courier scans at the pickup.
 *
 * LOG-06 publishes six — `picked_up | hub_arrival | hub_departure | out_for_delivery | delivered |
 * exception` — and the design's _"Parcel … is with you"_ is the moment a courier takes the parcel,
 * which is the first of them. The **value** is this application's reading of the design, as
 * `ARRIVED_AT_PICKUP` is of the slider on the screen before; the field is published and required.
 * Q-127.
 */
export const PICKED_UP = "picked_up" as const;

/**
 * The scan type `Start delivery` records.
 *
 * `out_for_delivery`, which is LOG-06's own word for the thing the button says. The button is
 * pressable — unlike the two on `Vehicle` — because everything the command needs is in this screen's
 * hand already: the shipment and the parcel it was told about, this device's next sequence number,
 * its position and its clock. Nothing is read from a service to press it, which is what makes a
 * device command a device command.
 */
export const OUT_FOR_DELIVERY = "out_for_delivery" as const;

/**
 * `Parcel par_3N0E is with you` — the design's headline.
 *
 * The identifier is the first eight characters of LOG-02's `parcelId`, which is the convention this
 * application already uses for a long identifier on a 320dp screen: `3 Offer` prints
 * `Offer ${offer.offerId.slice(0, 8)}`. The design's own token is eight characters, which is what
 * says the two agree rather than merely coincide.
 *
 * **Not `externalReference`**, although it is published, required, and reads far more like
 * `par_3N0E` than a UUID does. It is the *merchant's* reference for the parcel, and two merchants
 * may use the same one; the design's sentence is about the parcel the platform is tracking, and
 * the platform's name for it is the `parcelId`. The truncation is lossy and is recorded as Q-128,
 * with the note that the eight-character prefix of a v4 UUID is not unique and is shown to a human
 * rather than used as a key.
 *
 * `is with you` is the design's claim of custody, and it is the scan rather than the parcel: LOG-02
 * publishes `status: registered | sealed | retired`, none of which is "with a courier". So the
 * sentence is drawn when the scan was accepted and says something else when it was not.
 */
export function scanTitle(parcel: ParcelRecord | undefined): {
  readonly title: string;
  readonly absence: string | undefined;
} {
  if (parcel === undefined) {
    return {
      title: "This parcel",
      absence: "LOG-02 did not answer for this parcel, so it is not named.",
    };
  }
  return { title: `Parcel ${parcel.parcelId.slice(0, 8)} is with you`, absence: undefined };
}

/**
 * `Scan 1146 · next: Oyster Bay, 3.1 km` — the design's sub-line, with its second half named absent.
 *
 * The first half is verbatim and derived: `sourceSequence` is this device's own number and is the
 * one LOG-06 orders by, which is also what `6 Offline queue` prints as `Scan 1147`. The second half
 * is a sentence rather than a value, because nothing published can reach a next stop from a parcel —
 * see this module's header, which is the whole of the reasoning.
 */
export function scanSubLine(sequence: number): { readonly text: string; readonly designed: false } {
  return {
    text: `Scan ${String(sequence)} · the next stop isn't published`,
    designed: false,
  };
}

/**
 * The state chip. `['done','scan confirmed']` is the design's, for LOG-06's `recorded`.
 *
 * Total over the three published outcomes plus the one answer that is not an outcome, so a value
 * added to that enum fails the build here. The other three take this build's words and the screen
 * flags them:
 *
 *   - **`duplicate` is not a failure.** `6 Offline queue`'s own designed note says "Duplicates are
 *     fine and ignored", so the chip stays in the `done` class and the word changes. Drawing it as
 *     an error would teach a courier to re-scan, which is what produces duplicates.
 *   - **`stale` is a caution rather than an error.** A scan behind the latest was accepted and
 *     ignored; the parcel has moved on without it, which a courier needs to know and has not done
 *     wrong.
 *   - **A 201 publishes no body at all**, so there is no `outcome` to read. The contract describes
 *     that status as the scan having been created, which is `recorded` — but it is read off a status
 *     code rather than a published field, so the word says so rather than borrowing the design's.
 */
export function scanChip(outcome: ScanOutcome | undefined): {
  readonly class: ChipClass;
  readonly label: string;
} {
  switch (outcome) {
    case "recorded":
      return { class: "done", label: "scan confirmed" };
    case "duplicate":
      return { class: "done", label: "already scanned" };
    case "stale":
      return { class: "caution", label: "scan was late" };
    case undefined:
      return { class: "unknown", label: "scan accepted" };
  }
}

/** True when the chip is the design's own `['done','scan confirmed']`. */
export function scanChipIsDesigned(outcome: ScanOutcome | undefined): boolean {
  return outcome === "recorded";
}

/** Said when the screen was told no shipment, which is a state rather than a failure. */
export const NO_SHIPMENT_TITLE = "There's nothing to scan";
export const NO_SHIPMENT_SUB =
  "This screen needs the shipment the label names, and nothing published lets this app find out which one is in your hand.";

/**
 * Said when this device gave no position, so LOG-06's required `location` had no value to take.
 *
 * Not a failure of the platform and not a failure of the scan: the scan was never sent, because the
 * field is required and publishes two shapes — a hub, which a courier at a merchant's door is not
 * at, and a coordinate, which this device refused to give. Q-129.
 */
export const POSITIONLESS_TITLE = "We need your position to record this scan";
export const POSITIONLESS_SUB =
  "LOG-06 records a scan at a hub or at a coordinate, and your device gave neither. Turn location on for this app and scan again.";

/**
 * Why `Start delivery` cannot be pressed when the scan itself did not land.
 *
 * The button is drawn, because nothing may be removed. What it would send is a second scan of the
 * same shipment, and a sequence that goes out while the one before it is unaccounted for is the one
 * thing LOG-06 raises: "only a gap in the sequence is a problem, and we'll tell you" — the design's
 * own words, on `6 Offline queue`.
 */
export const START_DELIVERY_BLOCKED =
  "The pickup scan hasn't been accepted yet, and sending the next one would leave a gap in this device's sequence — which is the one thing LOG-06 raises.";
